import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import queryString from 'query-string';
import config from './common/config';
import ImageDisplay from './components/ImageDisplay';

import SocketioLayer from './SocketioLayer';
import {
	getExpandedPreference,
	getMessageObject,
	getMessageSummary,
	getParentCookie,
	permitMessageContentType,
	setExpandedPreference,
	setParentCookie,
	signPayload,
} from './common/Util';
import createLogger from './common/logger';
import MESSAGE_TYPE from './common/messageType';
import customerTokenFromParentCookie from './common/parentCookieToken';
import { attachReplyLocale, parseBrowserLanguages, resolveReplyLocale } from './common/replyLocale';
import { drainPendingSocketEmitQueue, emitOrQueueSocketEvent, hasPendingSocketEmit } from './common/socketEmitQueue';
import { color, fontSize } from './common/themes';
import Chat from './components/Chat';
import ChatInput from './components/ChatInput';
import IconX from './components/IconX';
import MessageContent from './components/MessageContent';
import Preview from './components/Preview';
import { setCustomerToken, setIsMobile, setSocketioClientOrgId } from './features/socketioSlice';
import { initializeLayoutConfig, setIsExpanded } from './features/layoutSlice';
import useResponsiveScale from './components/hooks/useResponsiveScale';
import useWidgetGeometry from './components/hooks/useWidgetGeometry';
import {
	EASE_IN,
	EASE_OUT,
	NOTIFY_IN_MS,
	PANEL_IN_MS,
	PRESS_MS,
	PANEL_OUT_MS,
	SETTLE_MS,
	usePresence,
	useReducedMotion,
} from './components/hooks/useTransition';
import { LAUNCHER_BOX, SURFACE_MIN_HEIGHT, Size } from './common/geometry';
import LivechatService from './services/LivechatService';

const useStyles = makeStyles(
	() => ({
		backdrop: {
			position: 'fixed' as const,
			top: 0,
			right: 0,
			bottom: 0,
			left: 0,
			backgroundColor: 'rgba(0, 0, 0, 0.5)',
		},
		/**
		 * 只給面板用：強制 Chat / Preview 填滿配給的高度。
		 *
		 * 它們的根容器高度是固定的 windowSize.height（580/754/928），只給 maxHeight
		 * 的話不會縮，而是被 overflow: hidden 裁掉 —— 底部的輸入框就這樣消失。
		 * 兩者內部都是 flex column + 訊息區 flex:1，所以拿到確定高度就會正確收縮
		 * （展開模式本來就走 height: 100%，同一條路徑）。
		 */
		panelFill: {
			'& > *': {
				height: '100%',
				maxHeight: '100%',
			},
		},
		/** 浮層（面板／通知）。位置由 useWidgetGeometry 算好後以 inline style 套上 */
		surface: (props: any) => ({
			position: 'fixed' as const,
			display: 'flex',
			flexDirection: 'column' as const,
			overflow: 'hidden',
			fontSize: fontSize.md * (props?.fontScale || 1),
		}),
		launcher: (props: any) => ({
			position: 'fixed' as const,
			top: 0,
			left: 0,
			width: LAUNCHER_BOX,
			height: LAUNCHER_BOX,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			// 位置用 transform 而不是 top/left —— 補間時不觸發 layout
			transition: props?.launcherTransition || 'none',
			// 觸控拖動時不要讓宿主頁跟著滾。範圍只有 80x80，不影響頁面其他區域
			touchAction: props?.isDraggable ? ('none' as const) : ('auto' as const),
			// 拖動時不要選到文字（會繼承給子元素）
			userSelect: 'none' as const,
			// iOS 長按會跳出系統選單並取消指標，拖動就中斷 —— 這裡不需要任何長按選單
			WebkitTouchCallout: 'none',
			cursor: props?.isDraggable ? (props?.isDragging ? 'grabbing' : 'grab') : 'pointer',
			/**
			 * 圖片完全不參與命中測試 —— 這是關鍵。
			 *
			 * <img> 預設可被瀏覽器原生拖曳，一旦 dragstart 贏過 setPointerCapture 就接管，
			 * 畫面上只會看到圖片的拖曳殘影、pointer 事件全斷（表現就是「有時候拖得動、
			 * 有時候拖不動」）。ImageDisplay 不轉傳 props 所以設不了 draggable={false}，
			 * 讓圖片不吃指標事件最乾脆：事件一律落到 80x80 的 launcher div，也就是把手本身。
			 */
			'& img': {
				pointerEvents: 'none' as const,
				WebkitUserDrag: 'none',
			},
			/**
			 * 按下的回饋（Intercom 的手感）：按下縮一點，放開彈回。
			 *
			 * 縮的是內層 iconStack，不是 launcher 本身 —— launcher 的 transform 拿來定位，
			 * 拖動時還會由 useWidgetGeometry 逐帧直接寫入，動到它一定打架。launcher 底下
			 * 只有 iconStack 一個子節點，所以 `> *` 就是它。
			 *
			 * hover 用 `:not(:active)` 排掉，按下時不必跟 hover 比特異性、也不依賴宣告順序；
			 * 外面再包 `@media (hover: hover)`，觸控裝置點完才不會卡在放大狀態。
			 */
			'& > *': {
				transition: props?.pressTransition || 'none',
			},
			'@media (hover: hover)': {
				'&:hover:not(:active) > *': {
					transform: 'scale(1.06)',
				},
			},
			'&:active > *': {
				transform: 'scale(0.88)',
			},
		}),
		icon: {
			width: '60px',
			height: '60px',
			borderRadius: '30px',
			cursor: 'inherit',
			transition: 'transform 0.16s linear 0s, opacity 0.08s linear 0s',
			border: 'none',
			boxShadow: '-5px 5px 8px 0 rgb(0 0 0 / 20%)',
		},
		/** logo 與關閉圖示疊在同一格，用 opacity 交叉淡入 */
		iconStack: {
			position: 'relative' as const,
			width: 60,
			height: 60,
		},
		iconLayer: (props: any) => ({
			position: 'absolute' as const,
			inset: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			transition: props?.iconTransition || 'none',
		}),
		closeBadge: {
			width: 60,
			height: 60,
			borderRadius: 30,
			backgroundColor: color.$7,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			boxShadow: '-5px 5px 8px 0 rgb(0 0 0 / 20%)',
		},
		showInput: {
			backgroundColor: color.$7,
			border: `1px solid ${color.$8}`,
			borderRadius: '12px',
		},
		iconRoundX: {
			cursor: 'pointer',
		},
		notifyRow: {
			border: `1px solid ${color.$8}`,
			borderRadius: '12px',
			backgroundColor: color.$7,
			display: 'flex',
			alignItems: 'center',
			padding: '16px 12px',
			marginBottom: 16,
			color: color.$13,
		},
		notifyImg: {
			borderRadius: '30px',
			marginRight: 8,
		},
		fiveLineWrap: (props: any) => ({
			display: '-webkit-box',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			'-webkit-box-orient': 'vertical',
			'-webkit-line-clamp': '5',
			wordWrap: 'break-word',
			fontSize: fontSize.md * (props?.fontScale || 1),
		}),
		closeIconRow: {
			display: 'flex',
			justifyContent: 'flex-end',
			marginBottom: 16,
		},
		previewMessage: {
			display: 'flex',
			justifyContent: 'flex-end',
		},
	}),
	{
		name: 'App',
	}
);

let timer: any = null;
const logger = createLogger('app');

const App = () => {
	const dispatch = useDispatch();

	const { browserLanguage, browserLanguages, encryptedOrgId, autoOpenLiveChat, alwaysOn } = queryString.parse(
		window.location.search
	);
	const parsedBrowserLanguages = parseBrowserLanguages(browserLanguages?.toString());
	const initialReplyLocale = resolveReplyLocale(
		browserLanguage?.toString() || navigator.language || null,
		parsedBrowserLanguages
	);

	const [isLoading, setIsloading] = useState(false);
	const [isInited, setIsInited] = useState(false);
	const [isBadgeShow, setIsBadgeShow] = useState(false);
	const [isShowPanel, setIsShowPanel] = useState(false);
	const [pendingInitialMessage, setPendingInitialMessage] = useState('');
	const [isStart, setIsStart] = useState(false);
	const [isOpenChat, setIsOpenChat] = useState(false);
	const [isGetLastMessage, setGetLastMessage] = useState(true);

	const [styleData, setStyleData]: any = useState(null);
	const [orgData, setOrgData]: any = useState(null);
	const [isShowBottomInfo, setIsShowBottomInfo]: any = useState(true);
	const [isSuper8, setIsSuper8]: any = useState(true);
	const [notifyMessage, setNotifyMessage]: any = useState(null);
	const [notifyHeight, setNotifyHeight] = useState(0);
	const [replyLocale] = useState(initialReplyLocale);
	const chatRef = useRef<any>(null);
	const messageRef = useRef<any>(null);

	const socketClient = useSelector((store: any) => store.socketio.client);
	const socketClientReady = useSelector((store: any) => store.socketio.ready);
	const isMobile = useSelector((store: any) => store.socketio.isMobile);
	const customerToken = useSelector((store: any) => store.socketio.customerToken);
	const canSendMessage = Boolean(customerToken && socketClient && socketClientReady);

	const { windowSize, fontScale } = useResponsiveScale();

	const isExpanded = useSelector((store: any) => store.layout?.isExpanded);

	const isExpandedActive = !isMobile && isExpanded && isShowPanel;
	const isImageViewerOpen = useSelector((store: any) => store.layout?.isImageViewerOpen);

	// onToggle 定義在後面，但 hook 的 tap callback 在這裡就要拿到它
	const onToggleRef = useRef<() => void>(() => undefined);

	const reducedMotion = useReducedMotion();
	const ms = (value: number) => (reducedMotion ? 0 : value);

	const panelPresence = usePresence(isShowPanel, ms(PANEL_OUT_MS));
	const isNotifyActive = Boolean(isBadgeShow && notifyMessage && customerToken);
	const notifyPresence = usePresence(isNotifyActive, ms(PANEL_OUT_MS));

	// 熱區與渲染都用 mounted 而不是原本的開關旗標 —— 關閉動畫進行中面板區域還不穿透
	const isNotifyVisible = notifyPresence.mounted && Boolean(notifyMessage && customerToken);
	const isPanelAnchored = panelPresence.mounted && !isMobile && !isExpandedActive;
	// 「面板自己佔滿可見範圍」—— 只有手機面板與展開模式
	const isPanelFullscreen = Boolean((isMobile && panelPresence.mounted) || isExpandedActive);
	/**
	 * 「熱區要涵蓋整個可見範圍」—— 比上面多了圖片檢視。
	 *
	 * 圖片檢視是 FileReviewer 用 Portal 疊在面板之上的全屏遮罩，面板本身位置尺寸都不該
	 * 動；但遮罩鋪滿畫面，熱區沒跟著放大就點不到、也關不掉。兩件事必須分開 —— 早先混成
	 * 一個旗標，圖片檢視一開就把 surfaceSize 清成 null，面板算不出矩形而退回 launcher
	 * 座標＋固定尺寸，整個縮到右下角只露出標題列一角。
	 */
	const isFullscreen = isPanelFullscreen || isImageViewerOpen;

	/**
	 * 通知泡泡寬度是已知的，只有高度要量；面板尺寸直接用 windowSize（Chat/Preview 就是照它設高）。
	 * 手機要近乎滿版：給無限寬，交給 placeSurface 夾成 viewport 扣左右 margin。
	 * 實際畫出的寬度一律取 surfaceRect.width，讓畫面與 clip-path 熱區同一個來源（S8CS-400）。
	 */
	const notifyWidth = isMobile ? Number.POSITIVE_INFINITY : windowSize.width;

	/**
	 * 面板不可壓縮的高度（標題列 + rich menu + 輸入列 + 留白）。
	 *
	 * 拖到畫面中段時單側只剩約半個視窗高，面板必須壓縮訊息區去適應 —— 但壓到連輸入列
	 * 都放不下就沒有意義了，那個底線就是這個值。rich menu 是後台可設的，寫死一定會在
	 * 某些 org 上失準，所以在執行期量。
	 */
	const [panelMinHeight, setPanelMinHeight] = useState(SURFACE_MIN_HEIGHT);

	const surfaceSize: Size | null = isPanelFullscreen
		? null
		: isPanelAnchored
		? windowSize
		: isNotifyVisible && notifyHeight
		? { width: notifyWidth, height: notifyHeight }
		: null;

	const {
		viewport,
		margins,
		launcher,
		surfaceRect,
		isDragging,
		isDraggable,
		startDrag,
		launcherElRef,
		surfaceElRef,
	} = useWidgetGeometry({
		orgId: String(encryptedOrgId || ''),
		styleData,
		surfaceSize,
		// 只有面板有可壓縮的訊息區；通知泡泡不傳，讓它以自身高度為底不被裁切
		surfaceMinHeight: isPanelAnchored ? panelMinHeight : undefined,
		fullscreen: isFullscreen,
		settleMs: ms(SETTLE_MS),
		onLauncherTap: () => onToggleRef.current(),
	});

	// 拖動中絕不套 transition，否則跟手會延遲
	const launcherTransition = isDragging || reducedMotion || !isDraggable ? 'none' : `transform ${SETTLE_MS}ms ${EASE_OUT}`;
	const iconTransition = reducedMotion ? 'none' : `opacity ${PANEL_IN_MS}ms ${EASE_OUT}`;
	const pressTransition = reducedMotion ? 'none' : `transform ${PRESS_MS}ms ${EASE_OUT}`;

	const classes = useStyles({ fontScale, isDragging, isDraggable, launcherTransition, iconTransition, pressTransition });

	// 通知泡泡的高度是內容決定的（樣板裡的圖片載入後還會變），所以用 ResizeObserver 追
	useEffect(() => {
		const element = messageRef.current;
		// ResizeObserver 的型別要到 TS 4.2 的 dom lib 才有，這專案鎖 4.1.4，所以從 window 取
		const Observer = (window as any).ResizeObserver;
		if (!isNotifyVisible || !element) {
			return undefined;
		}

		const measure = () => setNotifyHeight(element.offsetHeight || 0);
		measure();

		if (!Observer) {
			return undefined;
		}

		const observer = new Observer(measure);
		observer.observe(element);

		return () => observer.disconnect();
	}, [isNotifyVisible]);

	/**
	 * 量面板不可壓縮的高度：標題列、rich menu、輸入列 —— 也就是彈性訊息區以外的每一列。
	 *
	 * 刻意「加總兄弟節點」而不是「整體高度減去訊息區」。後者會回饋：量到的值會影響面板
	 * 高度，面板高度又決定訊息區高度，而 `offsetHeight` 取整 —— 訊息區拿到的是 flex
	 * 分配後的小數餘量，兩次讀取的進位方向可能差 1px，於是 setState 在 260/261 之間
	 * 來回，ResizeObserver 每次都再被觸發，永遠不收斂。實測輸入框長高時整個 renderer
	 * 凍住超過 45 秒。
	 *
	 * 加總兄弟節點跟面板高度無關（那幾列都是內容決定高度），量到的值不受自己影響，迴圈
	 * 從結構上就不存在。同時用 getBoundingClientRect 的小數值再 ceil，避免取整抖動。
	 *
	 * 需要追蹤的變動是真的：rich menu 的圖載入後會變高、輸入框打字會長高。
	 */
	useEffect(() => {
		const wrapper = surfaceElRef.current;
		const elastic = wrapper && (wrapper.querySelector('[data-elastic]') as HTMLElement | null);
		const Observer = (window as any).ResizeObserver;

		// Preview 自己就有 overflowY: auto，沒有可壓縮區也不需要底線
		if (!isPanelAnchored || !wrapper || !elastic) {
			return undefined;
		}

		const measure = () => {
			const siblings = Array.prototype.slice.call(elastic.parentElement?.children || []) as HTMLElement[];
			const rigid = siblings
				.filter(child => child !== elastic)
				.reduce((total, child) => total + child.getBoundingClientRect().height, 0);
			const floor = Math.ceil(rigid);

			if (floor <= 0) {
				return;
			}
			// 再保險一層：1px 以內的抖動不觸發 render，任何殘留的量測雜訊都無法形成迴圈
			setPanelMinHeight(previous => (Math.abs(previous - floor) > 1 ? floor : previous));
		};
		measure();

		if (!Observer) {
			return undefined;
		}

		const observer = new Observer(measure);
		observer.observe(elastic);

		return () => observer.disconnect();
	}, [isPanelAnchored, isOpenChat, surfaceElRef]);

	useEffect(() => {
		if (!isNotifyVisible) {
			setNotifyHeight(0);
		}
	}, [isNotifyVisible]);

	const isMobileRef = useRef(null);
	isMobileRef.current = isMobile;

	const isExpandedRef = useRef(false);
	isExpandedRef.current = isExpanded;

	const MA_SENDER = 'marketing_automation';

	const initCustomerToken = () => {
		const { customerToken: continuousCustomerToken } = queryString.parse(window.location.search);

		if (continuousCustomerToken) {
			dispatch(setCustomerToken(continuousCustomerToken));
		} else {
			getParentCookie(config.LOCAL_STORAGE_CUSTOMER_TOKEN);
		}
	};

	const onClearNotifyMessage = () => {
		if (isMobile) {
			window.parent.postMessage({ target: 'eightChat', command: 'notifyMobileHide' }, '*');
		} else {
			window.parent.postMessage({ target: 'eightChat', command: 'notifyDesktopHide' }, '*');
		}
	};

	const onReceiveMessage = (message: any) => {
		if (permitMessageContentType(message.contentType)) {
			setGetLastMessage(false);
			setTimeout(() => {
				setGetLastMessage(true);
			});

			if (!isShowPanel) {
				setIsBadgeShow(true);
				setNotifyMessage(message);
				setTimeout(() => {
					if (isMobile) {
						window.parent.postMessage(
							{
								target: 'eightChat',
								command: 'notifyMobileShow',
							},
							'*'
						);
					} else {
						// 過渡期給舊版 launch.js 的指令。新版靠 setFrameRect，外框直接反映泡泡高度，
						// 不再需要回傳 messageContentHeight 讓宿主頁去撐
						window.parent.postMessage({ target: 'eightChat', command: 'notifyDesktopShow' }, '*');
					}
				}, 500);

				clearTimeout(timer);
				timer = setTimeout(() => {
					setNotifyMessage(null);
					onClearNotifyMessage();
				}, 1000 * 20);
			}
		}

		if (message.contentType === MESSAGE_TYPE.SIGNAL) {
			logger.log('receive signal message', message);
			setParentCookie(
				`${MESSAGE_TYPE.SIGNAL}_${customerToken}_${message.data.stepId}_${message.data.procedureId}`,
				JSON.stringify(message.data),
				30
			);
		}
	};

	const openDesktopPanel = () => {
		window.parent.postMessage({ target: 'eightChat', command: 'openIcon' }, '*');
		if (isExpandedRef.current) {
			window.parent.postMessage({ target: 'eightChat', command: 'expandWindow' }, '*');
		}
	};

	const openIcon = () => {
		// because EventListener not get state value, so use Ref
		if (isMobileRef.current) {
			window.parent.postMessage({ target: 'eightChat', command: 'openImage' }, '*');
		} else {
			openDesktopPanel();
		}

		setIsShowPanel(true);
	};

	useEffect(() => {
		if (isShowPanel) {
			setIsBadgeShow(false);
			setIsStart(true);
		}
	}, [isShowPanel]);

	useEffect(() => {
		if (socketClient && socketClientReady) {
			socketClient.on('messageReceived', onReceiveMessage);
		}
		return () => {
			if (socketClient && socketClientReady) {
				socketClient.off('messageReceived', onReceiveMessage);
			}
		};
	}, [onReceiveMessage, socketClient, socketClientReady]);

	useEffect(() => {
		if (!socketClient || !socketClientReady || !hasPendingSocketEmit()) {
			return;
		}

		const queuedEvents = drainPendingSocketEmitQueue();
		logger.log('flush queued emits', {
			count: queuedEvents.length,
			connected: socketClient.connected,
			id: socketClient.id,
		});

		queuedEvents.forEach(({ eventType, data }) => {
			socketClient.emit(eventType, data);
		});
	}, [socketClient, socketClientReady]);

	async function commandHandler(command: any, options: any) {
		if (!command) {
			return;
		}
		switch (command) {
			case 'createNewCustomer':
				dispatch(setCustomerToken(''));
				break;

			case 'getParentCookie': {
				const token = customerTokenFromParentCookie(options, config.LOCAL_STORAGE_CUSTOMER_TOKEN);
				if (token !== null) {
					dispatch(setCustomerToken(token));
				}
				break;
			}

			case 'notifyOpenIcon':
				openIcon();
				if (options?.initialMessage) {
					setPendingInitialMessage(options.initialMessage);
				}
				break;
			default:
		}
	}

	const getParentMessage = async (event: any) => {
		const eventData = event?.data;
		if (eventData?.target !== 'eightChat') {
			return;
		}

		await commandHandler(eventData.command, eventData.options);
	};

	useEffect(() => {
		if (autoOpenLiveChat && isInited) {
			openIcon();
		}
	}, [autoOpenLiveChat, isInited]);

	useEffect(() => {
		if (encryptedOrgId) {
			dispatch(setSocketioClientOrgId(encryptedOrgId));

			const getSettingData = async () => {
				const res: any = await LivechatService.getSettingData(encryptedOrgId);

				if (res) {
					if (res.position === 'bottom-right') {
						window.parent.postMessage({ target: 'eightChat', command: 'rightContent', options: { styleData: res } }, '*');
					} else {
						window.parent.postMessage({ target: 'eightChat', command: 'leftContent', options: { styleData: res } }, '*');
					}
					setStyleData(res);
				}
			};
			const getOrgData = async () => {
				const res: any = await LivechatService.getOrgData(encryptedOrgId);
				if (res) {
					setOrgData(res);

					if (res?.enableGA4) {
						setIsStart(true);
					}
				}
			};
			const getShowBottomInfo = async () => {
				const res: any = await LivechatService.getShowBottomInfo(encryptedOrgId);
				if (res) {
					setIsShowBottomInfo(res.isShow);
					setIsSuper8(res.isSuper8);
				}
			};

			const init = async () => {
				setIsInited(false);
				setIsloading(true);
				initCustomerToken();
				dispatch(setIsMobile(/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)));
				dispatch(setIsExpanded(getExpandedPreference(String(encryptedOrgId))));
				await getSettingData();
				await getOrgData();
				await getShowBottomInfo();
				setIsloading(false);
				setIsInited(true);
			};
			init();

			window.addEventListener('message', getParentMessage);
		}
		return () => {
			window.removeEventListener('message', getParentMessage);
		};
	}, [encryptedOrgId]);

	// 初始化 layout 配置到 Redux
	useEffect(() => {
		if (styleData?.windowSizeLevel || styleData?.fontSizeLevel) {
			dispatch(
				initializeLayoutConfig({
					windowSizeLevel: styleData.windowSizeLevel,
					fontSizeLevel: styleData.fontSizeLevel,
				})
			);
		}
	}, [styleData, dispatch]);

	const onToggle = () => {
		if (alwaysOn && isShowPanel) {
			return;
		}

		if (isMobile) {
			if (!isShowPanel) {
				window.parent.postMessage({ target: 'eightChat', command: 'openImage' }, '*');
			} else {
				window.parent.postMessage({ target: 'eightChat', command: 'closeImage' }, '*');
			}
		} else if (!isShowPanel) {
			openDesktopPanel();
		} else {
			window.parent.postMessage({ target: 'eightChat', command: 'closeIcon' }, '*');
		}

		setIsShowPanel(!isShowPanel);
	};
	onToggleRef.current = onToggle;

	const onToggleExpand = () => {
		const next = !isExpandedRef.current;
		dispatch(setIsExpanded(next));
		setExpandedPreference(String(encryptedOrgId), next);
		window.parent.postMessage({ target: 'eightChat', command: next ? 'expandWindow' : 'collapseWindow' }, '*');
	};

	const onCloseBadge = () => {
		setIsBadgeShow(false);
		onClearNotifyMessage();
	};

	const onClose = () => {
		if (alwaysOn) {
			return;
		}

		if (isMobile) {
			window.parent.postMessage({ target: 'eightChat', command: 'closeImage' }, '*');
		} else {
			window.parent.postMessage({ target: 'eightChat', command: 'closeIcon' }, '*');
		}

		onClearNotifyMessage();
		setIsShowPanel(false);
	};

	const onOpenChat = () => {
		setIsOpenChat(true);
		window.parent.postMessage({ target: 'eightChat', command: 'livechatClick' }, '*');
	};

	const notifyOpenChat = () => {
		if (isMobile) {
			window.parent.postMessage({ target: 'eightChat', command: 'openImage' }, '*');
		} else {
			openDesktopPanel();
		}
		setIsBadgeShow(false);
		setIsShowPanel(true);
		onOpenChat();
	};

	const notifyAddMessage = (message: any) => {
		notifyOpenChat();
		setTimeout(() => {
			chatRef.current.notifyAddMessage(message);
		});
	};

	const notifySendMessage = (messageObject: any, eventType: any = 'message') => {
		if (eventType === 'message' && !canSendMessage) {
			logger.warn('block send before identity ready', {
				eventType,
				hasClient: Boolean(socketClient),
				socketClientReady,
				customerToken,
			});
			return;
		}

		const payloadMessageObject = eventType === 'message' ? attachReplyLocale(messageObject, replyLocale) : messageObject;
		let data = JSON.stringify(payloadMessageObject);

		// sign the payload
		data = signPayload(data);

		emitOrQueueSocketEvent(socketClient, socketClientReady, eventType, data);
	};

	const notifySubmitMessage = async (sendData: any, type = MESSAGE_TYPE.TEXT_PLAIN_TYPE) => {
		if (!canSendMessage) {
			logger.warn('block submit before identity ready', {
				hasClient: Boolean(socketClient),
				socketClientReady,
				customerToken,
			});
			return;
		}

		const messageObject = await getMessageObject(sendData, type);
		notifyAddMessage(messageObject);
		notifySendMessage(messageObject);
	};

	// openIcon 帶入的 initialMessage：等 socket／身分就緒後自動送出一次（等同客戶傳送）
	useEffect(() => {
		if (pendingInitialMessage && canSendMessage) {
			notifySubmitMessage({ content: pendingInitialMessage });
			setPendingInitialMessage('');
		}
	}, [pendingInitialMessage, canSendMessage]);

	useEffect(() => {
		if (customerToken && isOpenChat && socketClient && socketClientReady) {
			socketClient.emit('follow');
		}
	}, [customerToken, isOpenChat, socketClient, socketClientReady]);

	// 浮層的定位：全屏狀態走 viewport（展開模式保留 1rem 內縮），其餘由避讓計算決定
	const expandedSurfaceStyle = { top: '1rem', right: '1rem', bottom: '1rem', left: '1rem', zIndex: 1 };
	const mobileSurfaceStyle = { top: 0, right: 0, bottom: 0, left: 0, width: '100%' };

	// 面板：尺寸已知（Chat/Preview 就是照 windowSize 設高），位置由避讓計算決定。
	// maxHeight 綁住配給的高度，元素才不會長出熱區之外（舊版 .root 也是靠 maxHeight + overflow hidden）
	const panelSurfaceStyle = surfaceRect
		? {
				top: surfaceRect.top,
				left: surfaceRect.left,
				width: surfaceRect.width,
				height: surfaceRect.height,
		  }
		: { top: launcher.top, left: launcher.left, width: windowSize.width, height: windowSize.height };

	const surfaceStyle = isExpandedActive
		? expandedSurfaceStyle
		: isMobile && panelPresence.mounted
		? mobileSurfaceStyle
		: panelSurfaceStyle;

	/** 以 launcher 中心為原點，面板才會從 launcher 長出來（Intercom 的手感） */
	const panelOrigin = surfaceRect
		? `${Math.round(launcher.left + LAUNCHER_BOX / 2 - surfaceRect.left)}px ${Math.round(
				launcher.top + LAUNCHER_BOX / 2 - surfaceRect.top
		  )}px`
		: 'center';

	const panelAnim = {
		transformOrigin: isExpandedActive || isMobile ? 'center' : panelOrigin,
		transform: panelPresence.entered ? 'scale(1)' : 'scale(0.92)',
		opacity: panelPresence.entered ? 1 : 0,
		transition: reducedMotion
			? 'none'
			: `transform ${panelPresence.entered ? PANEL_IN_MS : PANEL_OUT_MS}ms ${
					panelPresence.entered ? EASE_OUT : EASE_IN
			  }, opacity ${panelPresence.entered ? PANEL_IN_MS : PANEL_OUT_MS}ms linear`,
		willChange: 'transform, opacity',
	};

	// 通知自 launcher 所在側滑入
	const notifyFromLeft = launcher.left + LAUNCHER_BOX / 2 < viewport.width / 2;
	const notifyAnim = {
		transform: notifyPresence.entered ? 'translate3d(0, 0, 0)' : `translate3d(${notifyFromLeft ? -16 : 16}px, 0, 0)`,
		opacity: notifyPresence.entered ? 1 : 0,
		transition: reducedMotion ? 'none' : `transform ${NOTIFY_IN_MS}ms ${EASE_OUT}, opacity ${NOTIFY_IN_MS}ms linear`,
		willChange: 'transform, opacity',
	};

	/**
	 * 通知泡泡的高度是內容決定的，要先渲染才量得到。所以寬度一定要先給（否則
	 * position:absolute 會縮成內容寬，量出來的高度是錯的），位置則等量到高度、
	 * 算出 surfaceRect 之後才給 —— 在那之前先隱藏，不要閃在畫面角落。
	 */
	const notifySurfaceStyle = {
		// 一定要跟熱區同寬，否則多出的部分被 clip-path 切掉；量到高度前先用 placeSurface 會算出的寬度
		width: surfaceRect ? surfaceRect.width : Math.min(notifyWidth, Math.max(viewport.width - margins.x * 2, 0)),
		// 同理，高度上限要跟 placeSurface 的可用高度一致（不能取 surfaceRect.height：量到的高度會被自己卡住、轉直後長不回來）
		maxHeight: Math.max(viewport.height - margins.y * 2, 0),
		overflow: 'auto' as const,
		...(surfaceRect
			? { top: surfaceRect.top, left: surfaceRect.left }
			: { top: launcher.top, left: launcher.left, visibility: 'hidden' as const }),
	};

	const isLauncherVisible = !(isMobile && panelPresence.mounted) && !isExpandedActive && !isImageViewerOpen;
	// alwaysOn 關不掉，顯示關閉圖示會誤導
	const showCloseIcon = isShowPanel && !alwaysOn;

	return (
		!isLoading &&
		styleData && (
			<>
				{isStart && <SocketioLayer />}
				{isExpandedActive && <div className={classes.backdrop} />}

				{panelPresence.mounted && (
					<div
						ref={surfaceElRef as any}
						className={`${classes.surface} ${classes.panelFill}`}
						style={{ ...surfaceStyle, ...panelAnim }}
					>
						{isOpenChat ? (
							<Chat
								ref={chatRef}
								styleData={styleData}
								orgData={orgData}
								replyLocale={replyLocale}
								onClose={() => onClose()}
								onToggleExpand={onToggleExpand}
								setIsOpenChat={setIsOpenChat}
								isShowBottomInfo={isShowBottomInfo}
								isSuper8={isSuper8}
								alwaysOn={alwaysOn}
							/>
						) : (
							<Preview
								styleData={styleData}
								isGetLastMessage={isGetLastMessage}
								orgData={orgData}
								onClose={() => onClose()}
								onToggleExpand={onToggleExpand}
								onOpenChat={onOpenChat}
								isShowBottomInfo={isShowBottomInfo}
								isSuper8={isSuper8}
								alwaysOn={alwaysOn}
							/>
						)}
					</div>
				)}

				{isNotifyVisible && (
					<div
						ref={el => {
							messageRef.current = el;
							surfaceElRef.current = el;
						}}
						className={classes.surface}
						style={{ ...notifySurfaceStyle, ...notifyAnim }}
					>
						<div className={classes.closeIconRow}>
							<IconX id="x" onClick={onCloseBadge} className={classes.iconRoundX} color="#4C4C4C" hoverColor="#4C4C4C" />
						</div>
						{notifyMessage.sender === MA_SENDER &&
						notifyMessage.contentType === MESSAGE_TYPE.TEMPLATE_TYPE &&
						notifyMessage.data.templateType !== 'text' ? (
							<div className={classes.previewMessage}>
								<MessageContent
									isPreview
									data={notifyMessage}
									replyLocale={replyLocale}
									submitMessage={notifySubmitMessage}
									isShowQuickReply={false}
									isBoxShadow={false}
								/>
							</div>
						) : (
							<div className={classes.notifyRow}>
								<ImageDisplay className={classes.notifyImg} src={styleData.logo} width={24} height={24} />
								<div className={classes.fiveLineWrap}>{getMessageSummary(notifyMessage, orgData.displayName)}</div>
							</div>
						)}
						{notifyMessage.sender !== MA_SENDER && (
							<ChatInput
								className={classes.showInput}
								addMessage={notifyAddMessage}
								submitMessage={notifySubmitMessage}
								sendMessage={notifySendMessage}
								replyLocale={replyLocale}
								canSend={canSendMessage}
							/>
						)}
					</div>
				)}

				{isLauncherVisible && (
					<div
						ref={launcherElRef as any}
						className={classes.launcher}
						style={{ transform: `translate3d(${launcher.left}px, ${launcher.top}px, 0)` }}
						// 可拖動時走 pointer 路徑（tap 由 pointerup 判定）；不可拖動就單純掛 click
						onPointerDown={isDraggable ? startDrag : undefined}
						onClick={isDraggable ? undefined : () => onToggleRef.current()}
						// img 預設可被瀏覽器原生拖曳，dragstart 一觸發就接管、pointer 事件斷掉
						onDragStart={event => event.preventDefault()}
					>
						<div className={classes.iconStack}>
							<div className={classes.iconLayer} style={{ opacity: showCloseIcon ? 0 : 1 }}>
								<ImageDisplay
									className={classes.icon}
									src={styleData.logo}
									width={60}
									height={60}
									id="eightChatButton"
									badgeShow={isBadgeShow}
									badgeColor="primary"
								/>
							</div>
							<div className={classes.iconLayer} style={{ opacity: showCloseIcon ? 1 : 0, pointerEvents: 'none' }}>
								<div className={classes.closeBadge}>
									<IconX id="launcherClose" className="" color={color.$13} hoverColor={color.$13} />
								</div>
							</div>
						</div>
					</div>
				)}
			</>
		)
	);
};

export default App;
